import type { SourceableRef } from '@misumi/shared'
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
// UID-552 (ADR-0170/ADR-0171) — `discard()`/`discardSlot()` notify the server.
// Goes through the single generated client (F0.2: no hand-rolled fetch, no
// second client), which also gets CSRF and 401 handling for free via
// auth-middleware. The abandon route itself is ADR-0108 D10 (NOT ADR-0107 —
// 0107 is the unrelated project-carry-in ADR; this citation used to be wrong
// here and is fixed as part of this rearchitecture).
import { apiClient } from '../api/client'
import type { components } from '../api/schema.gen'
import { getSessionState, subscribeSessionState } from '../auth/session-store'
import type { SessionState } from '../auth/session-store'
// UID-464 (ADR-0151) — the rehydrate-time price re-resolution seam. The
// reciprocal `import type { RfqBundleItem }` in that module is type-only and
// erased, so this pair is not a runtime cycle.
import { resolveBundleItemPrices } from './resolve-bundle-prices'

/**
 * A part queued for an RFQ, added from wherever it was found (Library, a
 * BOM, …) before the user has committed to building the actual request.
 * Deliberately the minimal shape `RfqReviewPage`'s `toReviewLine` needs —
 * this module lives in `lib/`, not the `rfq` vertical, so it must not
 * import that vertical's types (verticals depend on lib, never the
 * reverse; see docs/dev-guide's layering note).
 *
 * UID-260 gap #1/#2/#4 follow-up — `sourceable` is widened with the two
 * OPTIONAL pricing-preview fields (`indicativePrice`/`pricingProjection`) a
 * full `Sourceable` carries but a bare `SourceableRef` does not. Every
 * caller of `addItems`/`beginDraft` (`BomDetailPage`, `PartDetailPage`,
 * `PartsLibraryPage`, `StagingPage`, `WorkspacePage`, `NewRfqModal`, and
 * `RfqReviewPage.handleAddParts` via `toReviewLine`) already HOLDS a full
 * `Sourceable`/`LibrarySourceable` at the moment it queues a part — this
 * only widens the TYPE so those already-present fields survive into the
 * bundle instead of being narrowed away structurally; it requires no new
 * fetch anywhere.
 */
export interface RfqBundleItem {
  readonly sourceable: SourceableRef & {
    readonly indicativePrice?: components['schemas']['Price']
    readonly pricingProjection?: components['schemas']['PricingProjection']
    /** UID-335 follow-up — mirrors `ConfirmedStagedPart.sourceable.imageRef`
     * (`confirm-parts`'s wire shape, field-for-field): always a root-relative,
     * same-origin-safe path when present, never a raw absolute URL. */
    readonly imageRef?: string | null
    /**
     * UID-605 (ADR-0177 Amendment A2) — the part's RAW, SERVER-AGREEING IDENTITY.
     * The same three `library.part` columns (`name` / `catalog_sku` /
     * `customer_bom_id`) the server's own `linePartIdentity` snapshot is built
     * from, so `configurationPartSeed` resolves the SAME seed string on both
     * sides and the configurator footer's live estimate cannot disagree with the
     * price the save path recomputes.
     *
     * NOT a display label. `RfqBundleItem.name` below is the label (it may be a
     * client-invented `Item <8 hex of id>` for a nameless part) and is FORBIDDEN
     * as a pricing seed; these are the un-synthesized strings. Two homes for
     * "name" on one object is a real smell, justified here and documented at
     * both declarations (Amendment A6).
     *
     * Fourth application of the same optional-widening pattern as
     * `indicativePrice` / `pricingProjection` / `imageRef` above, with the same
     * recorded rationale: every caller already HOLDS a full `Sourceable` when it
     * queues a part, so this only widens the TYPE so already-present fields
     * survive. No new fetch anywhere.
     *
     * NO SCHEMA-VERSION BUMP, DELIBERATELY — do not "fix" this into a v4.
     * `sanitizeItemForPersist` persists `sourceable` via a REST SPREAD, so these
     * already survive to `sessionStorage`; `isPersistedRfqBundleItem` and
     * `toRuntimeSlot` are deliberately shallow and pass the rest through. Adding
     * an optional field with a rendered-for-absence path is exactly the case
     * ADR-0171 §D3 leaves unchecked, and ADR-0171's bump criterion is "the SLOT
     * shape becomes UNREPRESENTABLE, not merely malformed" — three optional
     * nullable strings make nothing unrepresentable, and absence of a seed
     * already has a defined path (ADR-0177 §4.3's un-seeded base). Decisively, a
     * bump ACTIVELY WIPES every in-flight buyer draft in every open tab
     * (ADR-0171 D1): destroying hand-picked draft baskets to correct an advisory
     * pre-quote number is strictly worse harm than the bug it fixes.
     */
    readonly name?: string
    readonly catalogSku?: string | null
    readonly customerBomId?: string | null
  }
  /** The DISPLAY LABEL for this part — possibly a client-invented `Item <8 hex
   * of id>` for a nameless one. FORBIDDEN as a pricing seed (ADR-0177 §4 /
   * Amendment A6): the server has never seen an invented label, so seeding from
   * it would guarantee footer-vs-saved disagreement precisely where identity is
   * weakest. The identity lives on `sourceable.name` above. */
  readonly name: string
  readonly sku: string | null
  /**
   * UID-530 — the quantity the queuing screen was SHOWING for this part (the
   * Parts Library row's own `quantity`). OPTIONAL: a screen that has no
   * meaningful quantity to hand (a bare BOM/part-detail "add to RFQ") simply
   * omits it and the compose screen's default of 1 stands, exactly as before.
   *
   * Consumed ONCE, by {@link RfqBundleValue.addItems}/{@link RfqBundleValue.beginDraft},
   * which seed `draft.quantityOverrides` for the parts they actually add. It is
   * therefore a seed VALUE, not a field the bundle keeps in sync — `quantityOverrides`
   * is the single home for "how many of this does the buyer want", and it is what
   * `sanitizeItemForPersist` persists (this field is deliberately dropped there,
   * so a rehydrate cannot re-seed over an edited quantity).
   */
  readonly quantity?: number
  /**
   * UID-553 — the CSV-seeded (or user-edited) staging configuration to
   * pre-populate this line's RFQ Configure step, carried from
   * `ConfirmedStagedPart.configuration` by `StagingPage`'s Create-RFQ hand-off.
   * OPTIONAL: a queue with no configuration to hand (a bare catalog part, a
   * BOM/part-detail "add to RFQ") omits it, and the line stays needs-Configure.
   *
   * Consumed ONCE, by {@link RfqBundleValue.addItems}/{@link RfqBundleValue.beginDraft}
   * (both via `seedDraftMetaFromItems`), which seed
   * `draft.specSelections[sourceableId]` FIRST-WINS — exactly the "consume once,
   * not kept in sync" contract {@link quantity} has with `quantityOverrides`. It
   * is a seed VALUE, not a field the bundle keeps; `specSelections` is the single
   * home for a line's configuration and is what `sanitizeItemForPersist`
   * persists. Deliberately DROPPED by `sanitizeItemForPersist` so a rehydrate
   * cannot re-seed over a buyer's Configure edit.
   */
  readonly specSeed?: SpecSelection
}

// lib-owned. Built ONLY from schema.gen (which lives in lib/api) +
// primitives — no import of the rfq vertical (ADR-0049 layering resolution).
type RfqRegion = components['schemas']['RfqRegion']
type RfqRequestedService = components['schemas']['RfqRequestedService']
type RfqShipMethod = components['schemas']['RfqShipMethod']
/** UID-260 (ADR-0085) — the RFQ builder's Configure-step output for one
 * draft line. Re-exported so the vertical never has to reach into
 * `lib/api/schema.gen` directly for this shape (same posture as the
 * `RfqDraftService`/`RfqRequestedService` aliases above). */
export type SpecSelection = components['schemas']['SpecSelection']

/** One add-on service on the in-progress draft. Structurally the canonical
 * shape the vertical's `RfqReviewService` aliases (ADR-0049's layering
 * resolution) — lib defines it, the vertical imports and re-exports it under
 * its own established name. */
export interface RfqDraftService {
  readonly localId: string
  readonly type: RfqRequestedService['type']
  readonly description: string
  readonly scope: RfqRequestedService['scope']
  readonly sourceableIds: readonly string[]
}

/** The compose-screen metadata that must survive navigation for Resume
 * (ADR-0049, UID-113 AC #2) — lifted out of `RfqReviewPage`'s local
 * `useState` and into this store so "the draft" is one coherent thing: the
 * queued parts AND the metadata that describes them. Per-SLOT since UID-552
 * (ADR-0170) — every {@link RfqDraftSlot} carries its own. */
export interface RfqDraftMeta {
  readonly needByDate: string // '' when unset
  readonly region: RfqRegion // ADR-0070: 'any' is the real default member, not an omitted/absent state
  readonly notes: string
  readonly quantityOverrides: Readonly<Record<string, number>> // keyed by sourceable id
  readonly services: readonly RfqDraftService[]
  // UID-117 shipping pre-selection (ADR-0053), refined by UID-201 (ADR-0076) — the
  // compose-screen choice that must survive navigation for Resume (ADR-0049), same as
  // need-by/region/notes. 'none' / '' both mean "omit on submit" (buildCreateRequest drops
  // them). `shippingAccountId` REFERENCES the saved shipping-account book (UID-206) —
  // resolve-on-write server-side — REPLACING the retired free-text carrier-account input.
  readonly shipMethod: RfqShipMethod | 'none'
  readonly shippingAccountId: string
  readonly shipToAddressId: string
  // UID-260 (ADR-0085) — the RFQ builder's per-line Configure selections, keyed by
  // sourceable id (same "keyed, not index-aligned" posture as `quantityOverrides`, so
  // it survives navigation/Resume exactly like every other draft-meta field). Absent
  // key = "needs Configure" — the gating signal `PartsReviewTable`/the submit CTA read.
  readonly specSelections: Readonly<Record<string, SpecSelection>>
}

export const EMPTY_RFQ_DRAFT_META: RfqDraftMeta = {
  needByDate: '',
  region: 'any',
  notes: '',
  quantityOverrides: {},
  services: [],
  shipMethod: 'none',
  shippingAccountId: '',
  shipToAddressId: '',
  specSelections: {},
}

/**
 * UID-552 (ADR-0170) — one independently-resumable draft RFQ under
 * construction. Before this ticket the store held exactly ONE of these,
 * implicitly, as a flat set of fields; every producer entry point
 * ("Create RFQ"/"Add to RFQ" across the app) merged into that single
 * shared draft forever, so parts queued from different screens/visits
 * collapsed into one request. A `RfqDraftSlot` is the unit that fixes
 * that: {@link RfqBundleValue.beginDraft} always mints a NEW one, never
 * looks up or merges into an existing one, and multiple slots coexist side
 * by side, each independently resumable (`activateDraft`), discardable
 * (`discardSlot`), or submittable.
 */
export interface RfqDraftSlot {
  /** Client-minted, STABLE for the slot's whole life (see `makeSlotId`). */
  readonly slotId: string
  readonly items: readonly RfqBundleItem[]
  readonly draft: RfqDraftMeta
  /** The server id once the eager `POST /rfq` resolves (ADR-0082) —
   * `undefined` until then, or if that create hasn't run/succeeded yet. */
  readonly rfqId: string | undefined
  readonly rfqNumber: string | undefined
  /** True while THIS slot's eager `POST /rfq` is in flight. NEVER
   * persisted (see the persistence section below) — a persisted `true`
   * would permanently deadlock this slot's eager-create guard after a
   * refresh, since nothing would ever flip it back to `false`. */
  readonly creating: boolean
  /** ISO timestamp, for Draft-tab ordering (newest first) — Phase 2
   * (the `/rfq` Draft-tab N-card UI) is what actually renders that order;
   * this store just stamps it at mint time so that UI never has to guess. */
  readonly createdAt: string
}

/** Client-minted slot identity. Checks for an existing id-minting helper in
 * the repo first (grepped for `randomUUID`/`nanoid`/`makeId` — none exists
 * as a shared utility; `checkout-domain.ts`'s `generateSimulatedTokenRef`
 * and `AddOnServicesCard.tsx` each inline the same
 * `crypto.randomUUID()`-with-fallback shape locally, so this follows that
 * established local-helper posture rather than introducing a new shared
 * module for one caller). */
let slotIdCounter = 0
function makeSlotId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  slotIdCounter += 1
  return `slot-${Date.now()}-${slotIdCounter}`
}

/**
 * ADR-0103 (UID-309 Gate 5 remediation) — `sessionStorage`-backed durability
 * for the bundle (D1/D2). Everything below this comment through
 * {@link clearPersistedBundle} is that persistence layer: a versioned
 * snapshot shape, a strict-tenant-isolation read (D3), and fail-open
 * read/write/clear wrappers (D4) — colocated in this file (not a separate
 * module) because it needs `RfqBundleItem`/`RfqDraftMeta`/`RfqDraftSlot`,
 * all defined above, and nothing outside this file needs it.
 */
const RFQ_BUNDLE_STORAGE_KEY = 'ff_rfq_bundle_v1'

/**
 * Persisted-payload schema version. **Bump this whenever the shape of anything
 * under `slots`/`draft` changes** — see `PersistedRfqBundleV2` for what happened
 * the one time it wasn't.
 *
 * Why the KEY still says `v1` while the payload is at 3: the version gate is
 * strictly better than a new key here. A stale snapshot fails the guard and
 * `clearPersistedBundle()` actively WIPES it (see the read path below); minting
 * `ff_rfq_bundle_v3` would instead leave the old blob orphaned in storage,
 * unread and uncollected. The key is an identity, the version is a contract —
 * only the contract moves.
 *
 * UID-552 (ADR-0171) bumped this from 2 to 3: the payload moved from ONE flat
 * `items`/`draft`/`draftRfqId`/`draftRfqNumber` set to a `slots` array (see
 * `PersistedRfqBundleV3` below). Unlike the 1→2 bump, this one has a LOSSLESS
 * migration path (`migrateV2ToV3`) rather than an outright discard — see that
 * function's doc comment.
 *
 * Three test files hardcode both the key and this version rather than importing
 * them (a deliberate encapsulation posture, with precedent in
 * `bulk-cart-warn-preference.test.ts`) — so a bump means editing them together:
 * `lib/rfq/rfq-bundle-context.test.tsx`, `verticals/parts/PartsLibraryPage.test.tsx`,
 * and `verticals/rfq/RfqReviewPage.test.tsx`. Grep the key to find them.
 *
 * UID-605 (ADR-0177 Amendment A3) — NOT bumped to 4 when
 * `RfqBundleItem.sourceable` gained `name`/`catalogSku`/`customerBomId`, and
 * that was the ruling, not an oversight. Three OPTIONAL fields that already
 * survive the rest-spread persist, that the shallow validators pass through
 * untouched, and that have a defined rendered-for-absence path make nothing
 * UNREPRESENTABLE — which is this version's own bump criterion (ADR-0171
 * §Consequences) and exactly what §D3 leaves unchecked. A bump here would
 * ACTIVELY WIPE every in-flight buyer draft in every open tab (ADR-0171 D1) to
 * correct an advisory pre-quote number: strictly worse harm than the bug.
 * `rfq-bundle-context.test.tsx` pins both halves — the new fields survive a v3
 * round trip, AND a v3 blob written WITHOUT them still rehydrates.
 */
const RFQ_BUNDLE_SCHEMA_VERSION = 3

/** The persisted item shape — `RfqBundleItem['sourceable']` with its two
 * OPTIONAL pricing-preview fields (`indicativePrice`/`pricingProjection`)
 * dropped. ADR-0103 D5: those are server-derived money, and persisting them
 * would render a stale price as current on rehydrate after a refresh —
 * exactly the money-path-integrity issue `f9d45e37` closed and ADR-0085 D1
 * forbids. Dropping them (rather than plumbing in a re-fetch-on-rehydrate,
 * which no seam in this codebase supports yet) is the safe, in-scope choice:
 * the UI already renders every state these fields' absence produces
 * (`PartsReviewTable`'s "needs Configure"/"Needs quote" cases) correctly
 * for a Sourceable that simply never carried a preview price. */
type PersistedRfqBundleItem = Omit<RfqBundleItem, 'sourceable'> & {
  readonly sourceable: SourceableRef
}

/** The persisted draft shape — `RfqDraftMeta` with every `specSelections`
 * entry's server-derived `unitPriceAmount`/`currency`/`indicativeUnitPrice`/
 * `pricingOutcome` (ADR-0103 D5, same rationale as `PersistedRfqBundleItem`
 * above) dropped; `specification` (the user's actual Configure choices —
 * real intent, not money, ADR-0117 D4/D5) survives as-is.
 *
 * UID-346 (ADR-0117 D5) — this used to persist the deprecated `attributes`
 * bag. `attributes` is now a SERVER-DERIVED READ projection of
 * `specification` — a value supplied on write is ignored, never persisted
 * (see `SpecSelection.attributes`'s doc comment) — so persisting it here
 * would durably store a field the server will never honor on write. Only
 * `specification` is real, round-trippable client intent. */
type PersistedRfqDraftMeta = Omit<RfqDraftMeta, 'specSelections'> & {
  readonly specSelections: Readonly<Record<string, Pick<SpecSelection, 'specification'>>>
}

/** One persisted slot — `RfqDraftSlot` minus `creating`, which is NEVER
 * persisted (see `RfqDraftSlot.creating`'s doc comment). */
type PersistedRfqDraftSlot = Omit<RfqDraftSlot, 'items' | 'draft' | 'creating'> & {
  readonly items: readonly PersistedRfqBundleItem[]
  readonly draft: PersistedRfqDraftMeta
}

/**
 * SCHEMA VERSION 3 (UID-552, ADR-0171) — the multi-draft payload. Replaces
 * the single flat `items`/`draft`/`draftRfqId`/`draftRfqNumber` set with a
 * `slots` array, one entry per independently-resumable draft, plus which one
 * (if any) is active.
 */
interface PersistedRfqBundleV3 {
  readonly version: typeof RFQ_BUNDLE_SCHEMA_VERSION
  /** ADR-0103 D3 — the session account id this snapshot belongs to. Read-time
   * enforcement compares this against the CURRENT session before ever
   * applying the snapshot to state; a mismatch is treated exactly like "no
   * persisted bundle" (never partially trusted). */
  readonly accountId: string
  readonly slots: readonly PersistedRfqDraftSlot[]
  readonly activeSlotId: string | undefined
}

/**
 * The RETIRED single-slot schema (versions 1 and 2's shape — this is what
 * version 2 looked like; a bare version-1 payload is a proper SUBSET of it
 * and was already discarded wholesale by the pre-UID-552 guard, so it stays
 * discarded here too). Kept ONLY so `readPersistedBundle` can detect a v2
 * snapshot and MIGRATE it (see `migrateV2ToV3`) instead of discarding a
 * buyer's in-progress draft across this deploy — the fix ADR-0171 makes for
 * the exact loss the old v1→v2 comment (see git history) used to lament.
 */
interface PersistedRfqBundleV2 {
  readonly version: 2
  readonly accountId: string
  readonly items: readonly PersistedRfqBundleItem[]
  readonly draft: PersistedRfqDraftMeta
  readonly draftRfqId: string | undefined
  readonly draftRfqNumber: string | undefined
}

function sanitizeItemForPersist(item: RfqBundleItem): PersistedRfqBundleItem {
  const { indicativePrice, pricingProjection, ...sourceable } = item.sourceable
  // UID-553 — `specSeed` (and the `quantity` seed) are DROPPED here on purpose:
  // both are one-shot values `addItems` already consumed into `draft`
  // (`specSelections` / `quantityOverrides`), and re-persisting them would let a
  // rehydrate re-seed over a buyer's later Configure/quantity edit. Only the
  // three durable fields survive; the rest of `RfqBundleItem` is left behind.
  return { sourceable, name: item.name, sku: item.sku }
}

function sanitizeDraftForPersist(draft: RfqDraftMeta): PersistedRfqDraftMeta {
  const specSelections: Record<string, Pick<SpecSelection, 'specification'>> = {}
  for (const [sourceableId, selection] of Object.entries(draft.specSelections)) {
    specSelections[sourceableId] = { specification: selection.specification }
  }
  return { ...draft, specSelections }
}

/** Per-slot sanitize — ADR-0103 D5's money-stripping, applied to one slot.
 * Deliberately drops `creating` (see `RfqDraftSlot.creating`'s doc comment)
 * by simply never copying it onto the returned object. */
function sanitizeSlotForPersist(slot: RfqDraftSlot): PersistedRfqDraftSlot {
  return {
    slotId: slot.slotId,
    items: slot.items.map(sanitizeItemForPersist),
    draft: sanitizeDraftForPersist(slot.draft),
    rfqId: slot.rfqId,
    rfqNumber: slot.rfqNumber,
    createdAt: slot.createdAt,
  }
}

/**
 * Per-ITEM validator for an UNTRUSTED persisted `items` array (second-pass
 * code review W1).
 *
 * `PersistedRfqBundleItem` is a compile-time type over a `JSON.parse` result,
 * so nothing guarantees an element is an object at all, let alone that it
 * carries `sourceable.id`. Every runtime consumer dereferences that chain
 * UNCONDITIONALLY — `addItems`'s dedupe `Set`, `removeItem`'s filter,
 * `seedDraftMetaFromItems`, `resolve-bundle-prices`'s `item.sourceable.kind`
 * read on the rehydrate-time price resolve, and `summarizeDraft`'s
 * `item.name` — so a persisted `items: [null]` used to clear
 * {@link isPersistedRfqDraftSlot} (which only checks `Array.isArray`), render
 * a card (length 1 satisfies {@link isOpenDraftSlot}), and then throw a
 * TypeError at first touch.
 *
 * Checks exactly what those consumers dereference: `sourceable` is an object
 * with a string `id`, `name` is a string, `sku` is a string or null. The
 * OPTIONAL fields (`kind`/`uom`/`rev`, the pricing-preview fields) are left
 * unchecked on purpose — every one of them already has a
 * rendered-for-absence path, so demanding them would discard real parts to
 * enforce a shape the UI does not need.
 */
function isPersistedRfqBundleItem(value: unknown): value is PersistedRfqBundleItem {
  if (!isPlainRecord(value)) return false
  if (!isPlainRecord(value.sourceable) || typeof value.sourceable.id !== 'string') return false
  return typeof value.name === 'string' && (value.sku === null || typeof value.sku === 'string')
}

/** Shallow per-slot validator — drops an individual malformed slot rather
 * than condemning the whole snapshot (a corrupt ONE draft must not cost the
 * buyer every other concurrent one). Checks the fields THIS version's
 * rehydrate logic actually reads, matching the existing shallow-validation
 * posture below.
 *
 * Deliberately does NOT descend into `draft`'s individual fields, nor into
 * `items`' individual elements — those are {@link toRuntimeDraftMeta}'s and
 * {@link isPersistedRfqBundleItem}'s jobs respectively (code-review W1, both
 * passes). The halves are complementary and the split is intentional: this
 * guard answers "is this recognizably a slot at all?" (no → drop it), while
 * the normalizer in {@link toRuntimeSlot} answers "is every field this slot's
 * runtime consumers will DEREFERENCE actually well-formed?" (no → repair it
 * in place: substitute the empty default for a bad metadata bag, drop the
 * individual bad ITEM from `items`).
 *
 * Why repair-in-place rather than dropping the whole slot on any bad item:
 * `items` is the only irreplaceable thing in a slot — the buyer picked those
 * parts by hand across several screens. Condemning a slot with three intact
 * parts because a fourth element got truncated mid-write would be a strictly
 * larger loss than dropping the one entry we genuinely cannot render, and it
 * is the same "don't condemn the snapshot" reasoning that made the per-slot
 * filter (rather than a whole-payload discard) right one level up. The
 * degenerate all-items-bad case lands on an itemless slot, which
 * {@link isOpenDraftSlot} already treats as no draft at all and which
 * `beginDraft`'s scratch-slot prune reclaims. */
function isPersistedRfqDraftSlot(value: unknown): value is PersistedRfqDraftSlot {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.slotId === 'string' &&
    Array.isArray(v.items) &&
    typeof v.draft === 'object' &&
    v.draft !== null &&
    typeof v.createdAt === 'string'
  )
}

/** ADR-0103 D4 — a deploy can ship a new `RfqDraftSlot` shape while an OLDER
 * snapshot still sits in a returning tab's `sessionStorage`; this is the
 * "treat mismatched shape as absent" floor the ADR's Consequences section
 * requires. Deliberately shallow. */
function isPersistedRfqBundleV3(value: unknown): value is PersistedRfqBundleV3 {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    v.version === RFQ_BUNDLE_SCHEMA_VERSION &&
    typeof v.accountId === 'string' &&
    Array.isArray(v.slots)
  )
}

function isPersistedRfqBundleV2(value: unknown): value is PersistedRfqBundleV2 {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    v.version === 2 &&
    typeof v.accountId === 'string' &&
    Array.isArray(v.items) &&
    typeof v.draft === 'object' &&
    v.draft !== null
  )
}

/**
 * UID-552 (ADR-0171) — a v2 payload IS exactly a v3 payload with one slot:
 * lift it into a single slot (fresh `slotId`, `createdAt: now`, `rfqId:
 * draftRfqId`, `rfqNumber: draftRfqNumber`, `creating: false`) and make it
 * active. LOSSLESS, unlike the v1→v2 bump this repo already lived through
 * once (that one discarded the whole snapshot — see the retired
 * `PersistedRfqBundleV2` comment in git history) — this preserves a buyer's
 * in-progress draft across the deploy instead of repeating that loss.
 */
function migrateV2ToV3(v2: PersistedRfqBundleV2): PersistedRfqBundleV3 {
  const slotId = makeSlotId()
  return {
    version: RFQ_BUNDLE_SCHEMA_VERSION,
    accountId: v2.accountId,
    slots: [
      {
        slotId,
        items: v2.items,
        draft: v2.draft,
        rfqId: v2.draftRfqId,
        rfqNumber: v2.draftRfqNumber,
        createdAt: new Date().toISOString(),
      },
    ],
    activeSlotId: slotId,
  }
}

/** The in-memory shape `readPersistedBundle` hands back after parsing +
 * validating + (if needed) migrating — never the raw wire shape. */
interface RehydratedBundle {
  readonly slots: readonly RfqDraftSlot[]
  readonly activeSlotId: string | undefined
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Code-review W1 — normalizes an UNTRUSTED persisted `draft` bag into a
 * well-formed {@link RfqDraftMeta}.
 *
 * `PersistedRfqDraftMeta` is a compile-time type over a runtime value that
 * came out of `JSON.parse` — nothing guarantees it still HAS
 * `quantityOverrides`/`specSelections`/`services`, or that they are the right
 * kind of thing. A partial/malformed bag used to rehydrate fine and then
 * throw at first USE — `seedDraftMetaFromItems`'s `Object.hasOwn(draft.
 * quantityOverrides, …)`, `removeItem`'s `draft.services.some(…)`, and
 * `review-domain.ts`'s reads all dereference these unconditionally — which
 * flatly contradicts this file's stated intent ("drop one malformed slot,
 * don't condemn the snapshot"): a TypeError at render condemns the whole
 * screen, not one slot.
 *
 * Spread-over-defaults handles the ABSENT case (a key JSON omitted simply
 * never overrides `EMPTY_RFQ_DRAFT_META`'s value); the three explicit
 * re-assignments below additionally handle the WRONG-TYPE case, which a
 * spread cannot (`{...defaults, ...{services: 'nope'}}` yields
 * `services: 'nope'`). Both failure modes therefore land on the same,
 * well-formed empty default rather than on a crash.
 */
function toRuntimeDraftMeta(persisted: PersistedRfqDraftMeta): RfqDraftMeta {
  const raw = persisted as unknown as Record<string, unknown>
  return {
    ...EMPTY_RFQ_DRAFT_META,
    ...persisted,
    quantityOverrides: isPlainRecord(raw.quantityOverrides)
      ? (raw.quantityOverrides as Readonly<Record<string, number>>)
      : EMPTY_RFQ_DRAFT_META.quantityOverrides,
    specSelections: isPlainRecord(raw.specSelections)
      ? (raw.specSelections as Readonly<Record<string, SpecSelection>>)
      : EMPTY_RFQ_DRAFT_META.specSelections,
    services: Array.isArray(raw.services)
      ? (raw.services as readonly RfqDraftService[])
      : EMPTY_RFQ_DRAFT_META.services,
  }
}

function toRuntimeSlot(persisted: PersistedRfqDraftSlot): RfqDraftSlot {
  return {
    slotId: persisted.slotId,
    // Code-review W1 (second pass) — NEVER the raw persisted array; a single
    // malformed element would throw at first dereference. See
    // `isPersistedRfqBundleItem` for what "well-formed" means here and why a
    // bad ITEM is dropped rather than costing the whole slot.
    items: persisted.items.filter(isPersistedRfqBundleItem),
    // Code-review W1 — NEVER the raw persisted bag; see `toRuntimeDraftMeta`.
    draft: toRuntimeDraftMeta(persisted.draft),
    rfqId: persisted.rfqId,
    rfqNumber: persisted.rfqNumber,
    // NEVER rehydrated as true — see `RfqDraftSlot.creating`'s doc comment.
    creating: false,
    createdAt: persisted.createdAt,
  }
}

/** ADR-0103 D4 — every failure mode (storage disabled/throwing, e.g. Safari
 * private mode or quota; absent key; unparseable JSON; a shape-mismatched
 * payload) degrades to "no persisted bundle" and returns `undefined`, never
 * throws. ADR-0103 D3 — a snapshot whose `accountId` doesn't match the
 * CALLER-supplied current session account id is treated identically to "no
 * persisted bundle": tenant isolation is enforced HERE, at the one read
 * site, rather than trusted to every future call site to re-check.
 *
 * UID-552 (ADR-0171) — now ALSO the migration boundary: a v2 snapshot for
 * the right tenant is lifted into a one-slot v3 shape (`migrateV2ToV3`)
 * rather than discarded; anything else invalid is still discarded wholesale.
 */
function readPersistedBundle(accountId: string): RehydratedBundle | undefined {
  try {
    const raw = window.sessionStorage.getItem(RFQ_BUNDLE_STORAGE_KEY)
    if (!raw) return undefined
    const parsed = JSON.parse(raw)

    let v3: PersistedRfqBundleV3 | undefined
    if (isPersistedRfqBundleV3(parsed)) {
      v3 = parsed
    } else if (isPersistedRfqBundleV2(parsed)) {
      v3 = migrateV2ToV3(parsed)
    } else {
      return undefined
    }

    if (v3.accountId !== accountId) return undefined

    const slots = v3.slots.filter(isPersistedRfqDraftSlot).map(toRuntimeSlot)
    return { slots, activeSlotId: v3.activeSlotId }
  } catch {
    return undefined
  }
}

/**
 * ADR-0103 D4 — a throwing write (storage disabled/quota) never propagates;
 * the bundle simply isn't durable for that change, which is the safe
 * direction (never a crash mid-render).
 *
 * Code-review C2.2 — but it is no longer SILENT. Returns whether the write
 * actually landed, so the caller can tell the buyer. The failure this closes
 * is specific and severe: once the snapshot exceeds the tab's
 * `sessionStorage` quota, EVERY subsequent write fails, persistence stops
 * dead, and a refresh loses every open draft — with the old bare `catch {}`
 * the buyer's only warning was the drafts being gone. Multi-draft (UID-552)
 * makes the snapshot grow with the number of concurrent drafts, so this is a
 * reachable state, not a theoretical one.
 *
 * Deliberately does NOT attempt eviction/cleanup: silently dropping one of
 * the buyer's drafts to make room for the others would be a worse failure
 * than telling them. Surfacing it is the whole scope.
 */
function writePersistedBundle(snapshot: PersistedRfqBundleV3): boolean {
  try {
    window.sessionStorage.setItem(RFQ_BUNDLE_STORAGE_KEY, JSON.stringify(snapshot))
    return true
  } catch {
    return false
  }
}

/**
 * Code-review C2.2 — the user-facing copy for a failed durability write,
 * surfaced through the app's established transient-failure convention (a
 * `showToast(…, 'error')`, exactly as `RfqReviewPage.handleShare` /
 * `handleCopyShareLink` surface their own non-blocking failures). Exported so
 * the one consumer (`AppShell`) and its test name the same string rather than
 * each spelling out a copy that could drift.
 *
 * Says what broke and what the buyer can do about it, and does NOT claim the
 * drafts are already lost — they are still in memory and still submittable in
 * THIS tab; only durability across a refresh is gone.
 */
export const RFQ_BUNDLE_PERSIST_FAILURE_MESSAGE =
  "Couldn't save your draft RFQs in this browser tab — if you reload, unsubmitted drafts may be lost. Submit or discard a draft to free up space."

/** ADR-0103 D3 — called on every session-identity change (login, logout, or
 * an account/tenant switch) so a stale entry can never be read back by a
 * LATER session in this tab, even one that happens to return to the same
 * account (a bundle must not survive a logout, full stop — see the ADR's
 * D3 text). Also called when a mismatched-tenant entry is discovered on
 * read, so it doesn't linger to be misread again later. */
function clearPersistedBundle(): void {
  try {
    window.sessionStorage.removeItem(RFQ_BUNDLE_STORAGE_KEY)
  } catch {
    // Intentionally swallowed — see doc comment above.
  }
}

/**
 * UID-552 (ADR-0170) — active-slot resolution, used by every flat
 * "active-slot projection" below. **Total**: given any `slots`/`activeSlotId`
 * pair, this always returns a definite answer, never a slot that doesn't
 * exist.
 *   1. If `activeSlotId` names a slot that's still there, use it.
 *   2. Else, if any slots exist, use the most-recently-created one — which,
 *      because `beginDraft` always APPENDS and slots are never reordered, is
 *      simply the last element.
 *   3. Else, there is no active slot at all (`undefined`).
 * Discarding the active slot re-points by this SAME rule automatically: no
 * explicit "pick a new active slot" step is needed anywhere, because every
 * caller re-derives the effective active slot through this function rather
 * than trusting the raw `activeSlotId` state directly.
 */
function resolveActiveSlotId(
  slots: readonly RfqDraftSlot[],
  activeSlotId: string | undefined,
): string | undefined {
  if (activeSlotId !== undefined && slots.some((slot) => slot.slotId === activeSlotId)) {
    return activeSlotId
  }
  return slots.length > 0 ? slots.at(-1)?.slotId : undefined
}

/**
 * Seeds a draft's two per-line bags from newly-queued items' own seed VALUES:
 * `draft.quantityOverrides` from `item.quantity` (UID-530 — the queuing
 * screen's own row quantity) and `draft.specSelections` from `item.specSeed`
 * (UID-553 — the CSV-seeded staging configuration carried by `StagingPage`'s
 * Create-RFQ hand-off).
 *
 * Pulled out to ONE pure function so both `beginDraft` (a brand-new slot) and
 * `addItems` (appending to the active slot) share the identical FIRST-WINS
 * seeding rule instead of hand-rolled copies drifting apart. When UID-553
 * added the `specSelections` seed it was written inline in `addItems` only —
 * which silently left `beginDraft` (the one entry point `StagingPage` actually
 * uses after UID-552) unseeded. Both rules live here now for exactly that
 * reason: adding a third seed must not require finding two call sites.
 *
 * FIRST-WINS for BOTH bags: an id that already has an entry keeps it — the
 * buyer may have edited the quantity or authored a configuration on the
 * compose screen and then gone back for more parts, and a re-add must never
 * reset their number back to the library's, nor their spec back to the CSV's.
 *
 * This is also what composes correctly with `beginDraft`'s scratch-slot
 * carry-forward: the `draft` passed in there is the INHERITED metadata, so an
 * inherited entry (real buyer intent, typed before any part was queued) wins
 * over a fresh seed for the same id, in both bags alike.
 *
 * Returns the SAME `draft` reference when nothing changes, so callers can
 * skip a pointless state update.
 */
function seedDraftMetaFromItems(
  draft: RfqDraftMeta,
  newItems: readonly RfqBundleItem[],
): RfqDraftMeta {
  // `Object.hasOwn`, NOT `in` (LOW-4): `'toString' in {}` is TRUE via the
  // prototype chain, so an `in` guard reads a sourceable id that happens to
  // collide with an Object.prototype member as already-seeded and silently
  // refuses to seed it. Sourceable ids are server-minted uuids today, so this
  // is defence rather than a live bug — but these bags are keyed by a value we
  // do not mint, and `hasOwn` is simply the correct question to ask of a record.
  const seeds = newItems.filter(
    (item) =>
      typeof item.quantity === 'number' &&
      Number.isInteger(item.quantity) &&
      item.quantity >= 1 &&
      !Object.hasOwn(draft.quantityOverrides, item.sourceable.id),
  )
  const specSeeds = newItems.filter(
    (item) =>
      item.specSeed !== undefined && !Object.hasOwn(draft.specSelections, item.sourceable.id),
  )
  if (seeds.length === 0 && specSeeds.length === 0) {
    return draft
  }
  const quantityOverrides = { ...draft.quantityOverrides }
  for (const item of seeds) {
    quantityOverrides[item.sourceable.id] = item.quantity as number
  }
  const specSelections = { ...draft.specSelections }
  for (const item of specSeeds) {
    specSelections[item.sourceable.id] = item.specSeed as SpecSelection
  }
  return { ...draft, quantityOverrides, specSelections }
}

/**
 * Has the buyer put anything real into this draft's metadata, or is it still
 * exactly {@link EMPTY_RFQ_DRAFT_META}? (Second-pass code review C1.)
 *
 * Needed by `beginDraft`'s scratch-slot prune, which must tell an
 * never-touched slot (safe to drop) from one the buyer typed a need-by date /
 * region / notes / shipping choice / add-on service into (whose metadata must
 * survive). A reference check against `EMPTY_RFQ_DRAFT_META` will not do:
 * `setDraft` merge-patches into a NEW object every time, so an identity
 * comparison reports "diverged" for a slot whose fields all still hold their
 * defaults.
 *
 * Deliberately a per-field record rather than a flat `||` chain, and
 * deliberately keyed by `keyof RfqDraftMeta`: adding a tenth field to
 * `RfqDraftMeta` then fails to COMPILE until someone decides whether it
 * counts as buyer intent. The alternative — a chain that silently ignores the
 * new field — is exactly how this defect would come back, since the failure
 * mode is silent data loss with no error anywhere.
 */
const DRAFT_META_DIVERGENCE: {
  readonly [K in keyof RfqDraftMeta]: (draft: RfqDraftMeta) => boolean
} = {
  needByDate: (draft) => draft.needByDate !== EMPTY_RFQ_DRAFT_META.needByDate,
  region: (draft) => draft.region !== EMPTY_RFQ_DRAFT_META.region,
  notes: (draft) => draft.notes !== EMPTY_RFQ_DRAFT_META.notes,
  shipMethod: (draft) => draft.shipMethod !== EMPTY_RFQ_DRAFT_META.shipMethod,
  shippingAccountId: (draft) => draft.shippingAccountId !== EMPTY_RFQ_DRAFT_META.shippingAccountId,
  shipToAddressId: (draft) => draft.shipToAddressId !== EMPTY_RFQ_DRAFT_META.shipToAddressId,
  services: (draft) => draft.services.length > 0,
  // The two keyed bags: any key at all is buyer intent. (On a ZERO-ITEM
  // scratch slot both are normally empty — the UI can only populate them
  // per-line — but a v2 migration or a hand-edited snapshot can produce
  // entries, and dropping them would be the same silent loss.)
  quantityOverrides: (draft) => Object.keys(draft.quantityOverrides).length > 0,
  specSelections: (draft) => Object.keys(draft.specSelections).length > 0,
}

function hasBuyerEnteredDraftMeta(draft: RfqDraftMeta): boolean {
  return Object.values(DRAFT_META_DIVERGENCE).some((diverged) => diverged(draft))
}

/**
 * App-wide "RFQ bundle" accumulator (UID-112, WS-4 §6 seam) — the
 * cross-screen counterpart to `cart-count-context`'s cart badge, but for
 * parts a user has flagged for a quote request before routing into
 * `/rfq/new`. Deliberately a thin, session-only, client-side store (no
 * server round-trip beyond the eager/abandon RFQ calls): the design
 * source's `rfqBundle` state is the same — a queue that only becomes a
 * real RFQ once the user reaches the review screen and submits.
 *
 * UID-552 (ADR-0170) re-architected this from ONE app-wide `items`/`draft`
 * pair into an array of independently-resumable {@link RfqDraftSlot}s.
 * Every producer entry point ("Create RFQ"/"Add to RFQ" across the app)
 * now calls {@link beginDraft}, which ALWAYS mints a genuinely new slot —
 * never looks up or merges into an existing one — so parts queued from
 * different screens/visits no longer collapse into one shared draft.
 *
 * The store still exposes a FLAT "active-slot projection" surface
 * (`items`/`draft`/`draftRfqId`/… and `addItems`/`setDraft`/…) with the
 * IDENTICAL names/signatures the pre-UID-552 store had, so `RfqReviewPage`,
 * `PartsReviewTable`, `RequestSummaryRail`, `ConfigurePartsModal`, and the
 * rest keep reading the exact fields they read before — they simply now
 * read/write whichever slot is ACTIVE rather than "the" bundle. See
 * {@link resolveActiveSlotId} for exactly how "active" is resolved, and the
 * "ASYNC RACE" note on the eager-create pattern below for why some writes
 * MUST bypass this flat surface and target a captured `slotId` directly.
 */
interface RfqBundleValue {
  // ---------------------------------------------------------------------
  // Multi-draft surface (UID-552/ADR-0170).
  // ---------------------------------------------------------------------
  readonly slots: readonly RfqDraftSlot[]
  readonly activeSlotId: string | undefined
  /** Mints a fresh slot seeded with `items`, makes it active, and returns
   * its `slotId`. NEVER looks up or merges into an existing slot — every
   * producer call is a genuinely NEW draft, full stop. Seeds
   * `draft.quantityOverrides` AND `draft.specSelections` from `items`
   * exactly like `addItems` does (see {@link seedDraftMetaFromItems}). */
  beginDraft(items: readonly RfqBundleItem[]): string
  /** Resume — makes an existing slot active (and kicks off that slot's
   * post-rehydrate price re-resolution the first time it's activated, per
   * ADR-0151). A no-op if `slotId` names no live slot. */
  activateDraft(slotId: string): void
  /** Discards exactly ONE slot: removes it locally AND fires a
   * fire-and-forget `POST /rfq/{rfqId}/abandon` for THAT slot's `rfqId`
   * only (never any sibling slot's). A no-op if `slotId` names no live
   * slot. See {@link discard} for the identical active-slot-only version,
   * and its doc comment for the full "which call site uses which" table. */
  discardSlot(slotId: string): void
  /** Slot-targeted write — see the "ASYNC RACE" note below. A no-op if
   * `slotId` no longer names a live slot (never resurrects a discarded
   * slot). */
  setSlotRfqId(slotId: string, id: string | undefined): void
  /** Slot-targeted write, sibling of `setSlotRfqId` — see the "ASYNC RACE"
   * note below. */
  setSlotRfqNumber(slotId: string, rfqNumber: string | undefined): void
  /** Slot-targeted write, sibling of `setSlotRfqId` — see the "ASYNC RACE"
   * note below. */
  setSlotCreating(slotId: string, value: boolean): void
  /**
   * Code-review C2.2 — set when the last `sessionStorage` write-through
   * FAILED (quota exceeded, storage disabled), `undefined` while durability
   * is healthy. Read by `AppShell`, which surfaces it as an error toast; the
   * store itself stays UI-free (nothing in `lib/` imports `components/`).
   * The in-memory bundle is unaffected — this reports only that a refresh
   * would no longer bring the drafts back.
   */
  readonly persistError: string | undefined
  /** Local-only reset of ONE named slot: REMOVES it. Never abandons (no
   * server call) — this is the "the RFQ just became a real customer
   * request, or the buyer explicitly wants to forget this draft's LOCAL
   * state without tombstoning a server row that was never minted" shape.
   * A no-op if `slotId` names no live slot. */
  clearSlot(slotId: string): void

  // ---------------------------------------------------------------------
  // ACTIVE-SLOT PROJECTIONS — identical names/signatures to the
  // pre-UID-552 single-bundle API. When there is NO active slot (no slots
  // exist, or `activeSlotId` names none), every read below returns the
  // same inert values the old empty bundle returned, and every mutator
  // below is a safe no-op.
  // ---------------------------------------------------------------------
  readonly items: readonly RfqBundleItem[]
  /** Adds items to the ACTIVE slot, deduped by `sourceable.id` — re-adding
   * a part already queued (e.g. clicking "Add to RFQ" twice from different
   * screens) is a no-op for that part, not a duplicate line.
   *
   * UID-552 — after the producer-entry-point migration to `beginDraft`,
   * `RfqReviewPage.handleAddParts` (the in-page "Add parts" modal) is the
   * ONE remaining caller of this. That is deliberate, not an oversight: the
   * modal legitimately adds MORE parts to the draft the user is already
   * editing on that screen — it is not a new "Create RFQ"/"Add to RFQ"
   * entry point, so it must not start a new slot. Do not add a second
   * caller without re-checking this distinction. */
  addItems(items: readonly RfqBundleItem[]): void
  /** Removes one queued part by `sourceable.id` from the ACTIVE slot — e.g.
   * `RfqReviewPage`'s "Remove" action, since the bundle (not page-local
   * state) is the actual source of truth for what's queued (UID-119). Also
   * cascades into that slot's `draft` (ADR-0049): drops the removed part's
   * `quantityOverrides` entry and strips its id from every service's
   * `sourceableIds` — AND, if that leaves a `specific_parts` service with
   * no targets left, drops the service entirely (PR review follow-up)
   * rather than leaving an empty-target ghost service for
   * `buildCreateRequest` to still emit, which would otherwise hit the
   * server's `specific_parts` cross-field 400 (ADR-0042 §5) with no
   * client-side blocker to catch it first. */
  removeItem(sourceableId: string): void
  /**
   * ACTIVE-SLOT local-only reset: identical to `clearSlot(activeSlotId)`.
   * NOT called merely on arriving at the review screen — the active slot
   * IS that screen's parts list, not a one-shot seed. Called once the
   * active slot has been submitted as a real RFQ, so a completed request
   * never reappears as a stale queue (UID-119).
   *
   * UID-552 — after the async-race fix (see below), the ONE remaining
   * caller of this flat form in app code is expected to become
   * `clearSlot(capturedSlotId)` instead (submit success must target the
   * slot captured at submit time, not "whichever slot happens to be
   * active when the await resolves"); `clear()` itself stays implemented
   * for API completeness / direct test use / the INERT no-provider value.
   */
  clear(): void
  /** The ACTIVE slot's compose-screen metadata. */
  readonly draft: RfqDraftMeta
  /** Merge-patches the ACTIVE slot's `draft` — page setters call this
   * instead of owning their own `useState` for these fields. */
  setDraft(patch: Partial<RfqDraftMeta>): void
  /**
   * UID-530 AC3 — merge SERVER-SNAPSHOTTED per-line configurations into the
   * ACTIVE slot's `draft.specSelections`, keyed by sourceable id.
   *
   * PRECEDENCE — an EXISTING entry always wins (never clobbers a selection
   * the buyer just authored in the Configure modal — see the pre-UID-552
   * history of this doc comment for the full rationale, unchanged here).
   */
  hydrateSpecSelections(harvested: Readonly<Record<string, SpecSelection>>): void
  /** The ACTIVE slot's `rfqId` (ADR-0082, UID-242 Item 1) — the server-side
   * draft RFQ minted EAGERLY on the compose screen's 0→1 parts transition.
   * `undefined` until a draft has been created for the active slot (or
   * after `clear()`/`discard()`). */
  readonly draftRfqId: string | undefined
  /**
   * Sets/clears the ACTIVE slot's `rfqId`.
   *
   * ⚠️ ASYNC RACE (UID-552/ADR-0170) — this reads "the active slot" AT CALL
   * TIME. If a caller invokes this from inside an `async` function AFTER an
   * `await`, and the user has navigated away and begun a DIFFERENT draft
   * during that await, this writes onto the WRONG slot — corrupting both
   * drafts (one gets a foreign server id, the other silently never gets
   * one). The rule: every write that happens after an `await` MUST capture
   * its `slotId` BEFORE the `await` and call {@link setSlotRfqId} with that
   * captured id, never this flat form. `RfqReviewPage`'s eager-create
   * effect and its submit handler's create-at-submit fallback both do this.
   */
  setDraftRfqId(id: string | undefined): void
  /** UID-309 — the ACTIVE slot's human-readable sequential number
   * (`RfqView.rfqNumber`, ADR-0093 C1, e.g. `RFQ-2026-0003`). Same
   * ASYNC-RACE caveat as `setDraftRfqId` — see that field's doc comment. */
  readonly draftRfqNumber: string | undefined
  /** Sets/clears the ACTIVE slot's `rfqNumber` — always called alongside
   * `setDraftRfqId`, never on its own. Same ASYNC-RACE caveat. */
  setDraftRfqNumber(rfqNumber: string | undefined): void
  /** ADR-0082 — true while the ACTIVE slot's eager `POST /rfq` is in
   * flight. Same ASYNC-RACE caveat as `setDraftRfqId`. */
  readonly creatingDraft: boolean
  setCreatingDraft(value: boolean): void
  /**
   * UID-464 (ADR-0151 D1) — true while the ACTIVE slot's post-rehydrate
   * price re-resolution is in flight. `false` when there was nothing to
   * rehydrate/resolve, which is the common case. See the pre-UID-552
   * history of this doc comment for the full rationale (unchanged) — the
   * only difference now is that the resolve is SLOT-SCOPED: a resolve that
   * lands after the user switched slots writes to the slot it was started
   * for, never to "whichever slot happens to be active now" (same
   * captured-id discipline as the ASYNC RACE note above).
   */
  readonly pricesResolving: boolean
  /**
   * ABANDON the ACTIVE slot: identical to `discardSlot(activeSlotId)`.
   * SUPERSEDES ADR-0049's mechanism (ADR-0108 D10's abandon route — see
   * this file's top-of-file citation note; the correct ADR is 0108, not
   * 0107, which is the unrelated project-carry-in ADR).
   *
   * WHICH CALL SITES USE WHICH — getting this wrong in either direction is
   * a serious bug, so it is enumerated here rather than left to be
   * rediscovered. UID-552 corrects this table: the pre-UID-552 version
   * enumerated only THREE call sites and OMITTED the fourth (the
   * moved-to-cart abandonment inside `removeLinesFromRfqAndMaybeDiscard`) —
   * that omission was itself a stale-doc defect, fixed here:
   *   | `RfqReviewPage` submit success                              | `clearSlot(captured)` | local only — NEVER abandons |
   *   | `RfqReviewPage.removeLinesFromRfqAndMaybeDiscard` (all lines moved to cart) | `discard()` (active slot) | abandons |
   *   | `RfqListPage` Draft-tab Discard                              | `discard()` (active slot; Phase 2 wires the per-card slotId) | abandons |
   *   | `PartsLibraryPage` banner "Clear" (confirmed)                 | `discard()` (active slot) | abandons |
   * The submit-success case is the dangerous one: the RFQ has just become
   * a real customer request, so abandoning there would tombstone it and
   * make it unreachable — which is exactly why it targets `clearSlot`
   * (never abandons), and why it captures the slot id at submit time
   * rather than going through this active-slot form (see the ASYNC RACE
   * note on `setDraftRfqId`).
   *
   * FAILURE TOLERANCE (AC3): the local reset happens FIRST and the abandon
   * is never awaited in a way that can block or fail the UI. If the call
   * fails — offline, 500, expired session — it is swallowed silently.
   */
  discard(): void
}

const RfqBundleContext = createContext<RfqBundleValue | undefined>(undefined)

export interface RfqBundleProviderProps {
  readonly children: ReactNode
  /** Test-only seam: starts the bundle already populated with ONE active
   * slot, so a test doesn't have to race a sibling effect (or call
   * `beginDraft` itself) to get items in before it reads them. Real app
   * code never passes this — the app always starts with NO slots. Ignored
   * when `initialSlots` is also passed. */
  readonly initialItems?: readonly RfqBundleItem[]
  /** Test-only seam, mirroring `initialItems` — starts that one initial
   * slot's `draft` already populated. Ignored when `initialSlots` is also
   * passed. */
  readonly initialDraft?: RfqDraftMeta
  /** UID-552 test-only seam for multi-slot tests: starts the bundle with
   * exactly these slots, with the most-recently-created one active (the
   * same rule {@link resolveActiveSlotId} always applies) — see that
   * function's doc comment. Real app code never passes this. */
  readonly initialSlots?: readonly RfqDraftSlot[]
}

export function RfqBundleProvider({
  children,
  initialItems = [],
  initialDraft = EMPTY_RFQ_DRAFT_META,
  initialSlots,
}: RfqBundleProviderProps) {
  const [slots, setSlots] = useState<readonly RfqDraftSlot[]>(() => {
    if (initialSlots !== undefined) return initialSlots
    if (initialItems.length === 0 && initialDraft === EMPTY_RFQ_DRAFT_META) return []
    return [
      {
        slotId: makeSlotId(),
        items: initialItems,
        draft: initialDraft,
        rfqId: undefined,
        rfqNumber: undefined,
        creating: false,
        createdAt: new Date().toISOString(),
      },
    ]
  })
  const [activeSlotId, setActiveSlotId] = useState<string | undefined>(() =>
    resolveActiveSlotId(slots, undefined),
  )
  // UID-464 (ADR-0151 D1) — the set of slot ids that have EVER started a
  // post-rehydrate/post-activate price re-resolution, so a slot only ever
  // fires that read once (repeated `activateDraft` calls on the same slot
  // must not re-fan-out reads every time the buyer switches back to it).
  const [resolvingSlotIds, setResolvingSlotIds] = useState<ReadonlySet<string>>(new Set())
  const resolveAttemptedSlotIdsRef = useRef<Set<string>>(new Set())

  // Render-synchronous mirrors of `slots`/`activeSlotId`, read by callbacks
  // that need "whichever slot is effectively active RIGHT NOW" without
  // closing over a stale render's values and without becoming unstable
  // `useCallback`s that re-render every consumer on every slot change.
  // Assigning during the render body (not inside an effect) is safe here:
  // React commits a render fully before any event handler can fire, so by
  // the time a user (or an `await`-free synchronous handler) reads these,
  // they already reflect the latest render.
  const slotsRef = useRef(slots)
  slotsRef.current = slots
  const activeSlotIdRef = useRef(activeSlotId)
  activeSlotIdRef.current = activeSlotId

  function currentActiveSlotId(): string | undefined {
    return resolveActiveSlotId(slotsRef.current, activeSlotIdRef.current)
  }

  // ADR-0103 (UID-309 Gate 5 remediation) — the session account id this
  // PROVIDER INSTANCE has resolved and is durably keyed to, or `null` while
  // unauthenticated. `undefined` (the initial value) means "not yet
  // observed" — see the pre-UID-552 history of this comment for the full
  // rationale (unchanged).
  const [accountId, setAccountId] = useState<string | null | undefined>(undefined)
  // Code-review C2.2 — see `RfqBundleValue.persistError`.
  const [persistError, setPersistError] = useState<string | undefined>(undefined)
  const hasHydratedRef = useRef(false)
  const lastIdentityRef = useRef<string | null | undefined>(undefined)

  /**
   * UID-464 (ADR-0151 D1) — ADR-0103 D5's second half: "prices must
   * re-resolve from the server on rehydrate," now SLOT-SCOPED (UID-552):
   * the write lands on the slot this resolve was started FOR
   * (`slotId`, captured at call time), never on "whichever slot is active
   * when the read finishes" — the same captured-id discipline the
   * ASYNC RACE note on `setDraftRfqId` describes. A resolve that lands
   * after the user switched slots therefore cannot corrupt the
   * now-active slot, and cannot silently vanish either (it still lands,
   * just on the right slot).
   */
  const resolveSlotPrices = useCallback((slotId: string, rehydrated: readonly RfqBundleItem[]) => {
    if (rehydrated.length === 0) return
    if (resolveAttemptedSlotIdsRef.current.has(slotId)) return
    resolveAttemptedSlotIdsRef.current.add(slotId)
    setResolvingSlotIds((current) => new Set(current).add(slotId))
    void (async () => {
      try {
        const resolved = await resolveBundleItemPrices(rehydrated)
        const pricedById = new Map(resolved.map((item) => [item.sourceable.id, item]))
        setSlots((current) =>
          current.map((slot) => {
            if (slot.slotId !== slotId) return slot
            // MERGE BY ID — never replace the array wholesale. This runs
            // after an await, so `resolved` is a picture of the SLOT as it
            // was when the reads STARTED: replacing wholesale would
            // silently revert any part added/removed from THIS slot
            // during the round trip. Only the two pricing fields may come
            // from here; membership is whatever the slot's CURRENT items
            // say it is.
            let changed = false
            const items = slot.items.map((item) => {
              const priced = pricedById.get(item.sourceable.id)
              if (priced !== undefined && priced !== item) {
                changed = true
                return priced
              }
              return item
            })
            return changed ? { ...slot, items } : slot
          }),
        )
      } finally {
        setResolvingSlotIds((current) => {
          if (!current.has(slotId)) return current
          const next = new Set(current)
          next.delete(slotId)
          return next
        })
      }
    })()
  }, [])

  // ADR-0103 D1/D3 — rehydrates this tab's persisted bundle ONCE, on the
  // first AUTHENTICATED session identity this provider instance ever
  // observes, and thereafter enforces D3 — an account/tenant switch OR a
  // logout, at any point after that while mounted, wipes ALL slots + the
  // persisted entry + `activeSlotId`, never rehydrating a DIFFERENT
  // identity's (or the SAME identity's post-logout) leftover snapshot.
  useEffect(() => {
    function handleIdentity(state: SessionState) {
      const identityKey =
        state.status === 'authenticated' ? state.session.accountContext.accountId : null

      if (!hasHydratedRef.current) {
        if (identityKey === null) {
          lastIdentityRef.current = null
          setAccountId(null)
          return
        }
        hasHydratedRef.current = true
        lastIdentityRef.current = identityKey
        setAccountId(identityKey)
        const persisted = readPersistedBundle(identityKey)
        if (persisted) {
          setSlots(persisted.slots)
          setActiveSlotId(persisted.activeSlotId)
          // UID-464 (ADR-0151 D1) — only the EFFECTIVE active slot's prices
          // re-resolve on mount (Only the compose screen renders prices, so
          // do not fan out N round trips for every persisted slot). A
          // non-active slot re-resolves lazily when it is activated via
          // `activateDraft`.
          const activeId = resolveActiveSlotId(persisted.slots, persisted.activeSlotId)
          const activeSlot = persisted.slots.find((slot) => slot.slotId === activeId)
          if (activeSlot) {
            resolveSlotPrices(activeSlot.slotId, activeSlot.items)
          }
        } else {
          clearPersistedBundle()
        }
        return
      }

      if (lastIdentityRef.current !== identityKey) {
        lastIdentityRef.current = identityKey
        // D3 — login, logout, AND a tenant switch all land here identically:
        // wipe every in-memory slot and the persisted entry.
        clearPersistedBundle()
        setSlots([])
        setActiveSlotId(undefined)
        setAccountId(identityKey)
      }
    }

    handleIdentity(getSessionState())
    return subscribeSessionState(handleIdentity)
    // Intentionally mount-only — see the pre-UID-552 history of this
    // comment for the exhaustive-deps rationale (unchanged); `resolveSlotPrices`
    // is a stable `useCallback` with an empty dependency list.
  }, [resolveSlotPrices])

  // ADR-0103 D1 — write-through: any change to the bundle's persisted-worthy
  // state re-serializes the WHOLE (v3) snapshot. Guarded on `accountId`
  // being a real string so this never writes anything for an
  // unauthenticated session, nor races ahead of the rehydrate-on-mount
  // effect above.
  //
  // Code-review C2.2 — the write's OUTCOME is now recorded, so a failure
  // (quota/disabled storage) can be surfaced to the buyer instead of
  // silently ending durability. Setting the same value twice is a React
  // no-op, so a persistently-failing write cannot loop this effect.
  useEffect(() => {
    if (typeof accountId !== 'string') return
    const wrote = writePersistedBundle({
      version: RFQ_BUNDLE_SCHEMA_VERSION,
      accountId,
      slots: slots.map(sanitizeSlotForPersist),
      activeSlotId,
    })
    setPersistError(wrote ? undefined : RFQ_BUNDLE_PERSIST_FAILURE_MESSAGE)
  }, [accountId, slots, activeSlotId])

  const beginDraft = useCallback((items: readonly RfqBundleItem[]): string => {
    const slotId = makeSlotId()
    const createdAt = new Date().toISOString()
    // Code-review C1 — drop any never-populated scratch slot as this real
    // draft is minted. `RfqReviewPage`'s mount-time mint (see its own
    // comment) creates an empty slot for a direct/bookmarked `/rfq/new`
    // visit to write metadata into; if the buyer then queues parts from
    // Library/BOM instead, that scratch slot would linger in state and in
    // the persisted snapshot forever — invisible (it is filtered out of
    // `countOpenDrafts` and the Draft tab, see `isOpenDraftSlot`) but real.
    //
    // SECOND-PASS CODE REVIEW C1 — the prune predicate alone was a data-loss
    // bug, and the comment that used to sit here ("pruning … cannot lose
    // buyer intent") was simply false. `items.length === 0 && rfqId ===
    // undefined && !creating` does NOT mean "nothing was ever put in it": the
    // compose screen's `RequestDetailsSection` renders unconditionally, so a
    // buyer who lands on `/rfq/new` directly can fill in need-by date,
    // region, notes, shipping and add-on services on a slot that still holds
    // ZERO items. Every EXTERNAL producer entry point then calls `beginDraft`
    // — and the prune discarded that slot with all of it inside.
    //
    // So the slot is still pruned (an invisible orphan is not an acceptable
    // outcome either — the buyer can never navigate back to a zero-item slot,
    // so merely KEEPING it strands the metadata just as thoroughly, and
    // splits one perceived draft into two), but its `draft` is CARRIED
    // FORWARD onto the newly-minted slot. That matches what the buyer
    // actually did: they typed a need-by date, then added parts to "their"
    // RFQ, and expect the date to still be there.
    //
    // THE ACTUAL INVARIANTS THIS HOLDS:
    //   1. A pruned slot never had an `rfqId` and was never `creating`, so
    //      pruning cannot orphan a server draft. A slot that HAS an `rfqId`
    //      and was emptied by `removeItem` is NOT pruned — its server row is
    //      real and only `discardSlot` may abandon it.
    //   2. A pruned slot held no items, so no queued part is ever lost.
    //   3. A pruned slot's buyer-entered metadata is not lost either — it
    //      moves onto the new slot. Nothing is DISCARDED by this prune except
    //      a slot that is empty in every respect.
    setSlots((current) => {
      const inheritable: RfqDraftMeta[] = []
      const kept = current.filter((slot) => {
        const isScratch = slot.items.length === 0 && slot.rfqId === undefined && !slot.creating
        if (!isScratch) return true
        if (hasBuyerEnteredDraftMeta(slot.draft)) inheritable.push(slot.draft)
        return false
      })
      // `.at(-1)`: slots are append-only and never reordered (see
      // `resolveActiveSlotId`), so the last one is the most recent — the
      // buyer's newest intent wins if somehow more than one scratch slot
      // carries metadata. Seeding runs OVER the inherited bag so the
      // FIRST-WINS rule still holds against an inherited quantity override
      // (UID-530) AND an inherited spec selection (UID-553) — see
      // `seedDraftMetaFromItems`.
      const inherited = inheritable.at(-1) ?? EMPTY_RFQ_DRAFT_META
      const newSlot: RfqDraftSlot = {
        slotId,
        items,
        draft: seedDraftMetaFromItems(inherited, items),
        rfqId: undefined,
        rfqNumber: undefined,
        creating: false,
        createdAt,
      }
      return [...kept, newSlot]
    })
    setActiveSlotId(slotId)
    return slotId
  }, [])

  const setSlotRfqId = useCallback((slotId: string, id: string | undefined) => {
    setSlots((current) =>
      current.map((slot) => (slot.slotId === slotId ? { ...slot, rfqId: id } : slot)),
    )
  }, [])

  const setSlotRfqNumber = useCallback((slotId: string, rfqNumber: string | undefined) => {
    setSlots((current) =>
      current.map((slot) => (slot.slotId === slotId ? { ...slot, rfqNumber } : slot)),
    )
  }, [])

  const setSlotCreating = useCallback((slotId: string, value: boolean) => {
    setSlots((current) =>
      current.map((slot) => (slot.slotId === slotId ? { ...slot, creating: value } : slot)),
    )
  }, [])

  const clearSlot = useCallback((slotId: string) => {
    setSlots((current) => current.filter((slot) => slot.slotId !== slotId))
    // Code-review follow-up — a removed slot's id can never be needed by the
    // once-per-slot price-resolve guard again (slot ids are never reused, see
    // `makeSlotId`), so drop it rather than growing this Set for the life of
    // the tab.
    resolveAttemptedSlotIdsRef.current.delete(slotId)
  }, [])

  const discardSlot = useCallback((slotId: string) => {
    const slot = slotsRef.current.find((candidate) => candidate.slotId === slotId)
    if (!slot) return
    setSlots((current) => current.filter((candidate) => candidate.slotId !== slotId))
    // See `clearSlot` above.
    resolveAttemptedSlotIdsRef.current.delete(slotId)
    if (slot.rfqId === undefined) {
      // No server draft was ever minted for this slot — nothing to abandon.
      return
    }
    // FIRE AND FORGET — see `discard()`'s FAILURE TOLERANCE doc comment.
    void apiClient
      .POST('/rfq/{rfqId}/abandon', { params: { path: { rfqId: slot.rfqId } } })
      .catch(() => {
        // Intentionally swallowed — the local slot is already removed, and
        // ADR-0108 D1's reaper collects the residual row.
      })
  }, [])

  const activateDraft = useCallback(
    (slotId: string) => {
      const slot = slotsRef.current.find((candidate) => candidate.slotId === slotId)
      if (!slot) return
      setActiveSlotId(slotId)
      resolveSlotPrices(slotId, slot.items)
    },
    [resolveSlotPrices],
  )

  // `currentActiveSlotId` reads `slotsRef`/`activeSlotIdRef` (render-synchronous mirrors),
  // never render state directly — listing it would recreate this callback (and every
  // consumer memoized on it) on every slot change for no behavioral difference. Same
  // posture as RfqReviewPage's eager-create effect a few files over.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see the comment above.
  const addItems = useCallback((newItems: readonly RfqBundleItem[]) => {
    const activeId = currentActiveSlotId()
    if (activeId === undefined) return
    setSlots((current) =>
      current.map((slot) => {
        if (slot.slotId !== activeId) return slot
        const existingIds = new Set(slot.items.map((item) => item.sourceable.id))
        const additions = newItems.filter((item) => !existingIds.has(item.sourceable.id))
        const items = additions.length > 0 ? [...slot.items, ...additions] : slot.items
        const draft = seedDraftMetaFromItems(slot.draft, newItems)
        if (items === slot.items && draft === slot.draft) return slot
        return { ...slot, items, draft }
      }),
    )
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: see `addItems` above.
  const removeItem = useCallback((sourceableId: string) => {
    const activeId = currentActiveSlotId()
    if (activeId === undefined) return
    setSlots((current) =>
      current.map((slot) => {
        if (slot.slotId !== activeId) return slot
        const items = slot.items.filter((item) => item.sourceable.id !== sourceableId)
        const draft = slot.draft
        if (
          items === slot.items &&
          !Object.hasOwn(draft.quantityOverrides, sourceableId) &&
          !Object.hasOwn(draft.specSelections, sourceableId) &&
          !draft.services.some((service) => service.sourceableIds.includes(sourceableId))
        ) {
          return slot
        }
        const nextOverrides = { ...draft.quantityOverrides }
        delete nextOverrides[sourceableId]
        const nextSpecSelections = { ...draft.specSelections }
        delete nextSpecSelections[sourceableId]
        return {
          ...slot,
          items,
          draft: {
            ...draft,
            quantityOverrides: nextOverrides,
            specSelections: nextSpecSelections,
            services: draft.services
              .map((service) => ({
                ...service,
                sourceableIds: service.sourceableIds.filter((id) => id !== sourceableId),
              }))
              .filter(
                (service) => service.scope !== 'specific_parts' || service.sourceableIds.length > 0,
              ),
          },
        }
      }),
    )
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: see `addItems` above.
  const setDraft = useCallback((patch: Partial<RfqDraftMeta>) => {
    const activeId = currentActiveSlotId()
    if (activeId === undefined) return
    setSlots((current) =>
      current.map((slot) =>
        slot.slotId === activeId ? { ...slot, draft: { ...slot.draft, ...patch } } : slot,
      ),
    )
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: see `addItems` above.
  const hydrateSpecSelections = useCallback(
    (harvested: Readonly<Record<string, SpecSelection>>) => {
      const activeId = currentActiveSlotId()
      if (activeId === undefined) return
      setSlots((current) =>
        current.map((slot) => {
          if (slot.slotId !== activeId) return slot
          const additions = Object.entries(harvested).filter(
            ([sourceableId]) => !Object.hasOwn(slot.draft.specSelections, sourceableId),
          )
          if (additions.length === 0) return slot
          return {
            ...slot,
            draft: {
              ...slot.draft,
              specSelections: {
                ...slot.draft.specSelections,
                ...Object.fromEntries(additions),
              },
            },
          }
        }),
      )
    },
    [],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: see `addItems` above.
  const setDraftRfqId = useCallback(
    (id: string | undefined) => {
      const activeId = currentActiveSlotId()
      if (activeId !== undefined) setSlotRfqId(activeId, id)
    },
    [setSlotRfqId],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: see `addItems` above.
  const setDraftRfqNumber = useCallback(
    (rfqNumber: string | undefined) => {
      const activeId = currentActiveSlotId()
      if (activeId !== undefined) setSlotRfqNumber(activeId, rfqNumber)
    },
    [setSlotRfqNumber],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: see `addItems` above.
  const setCreatingDraft = useCallback(
    (value: boolean) => {
      const activeId = currentActiveSlotId()
      if (activeId !== undefined) setSlotCreating(activeId, value)
    },
    [setSlotCreating],
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: see `addItems` above.
  const clear = useCallback(() => {
    const activeId = currentActiveSlotId()
    if (activeId !== undefined) clearSlot(activeId)
  }, [clearSlot])

  // biome-ignore lint/correctness/useExhaustiveDependencies: see `addItems` above.
  const discard = useCallback(() => {
    const activeId = currentActiveSlotId()
    if (activeId !== undefined) discardSlot(activeId)
  }, [discardSlot])

  const effectiveActiveSlot = useMemo(() => {
    const id = resolveActiveSlotId(slots, activeSlotId)
    return id !== undefined ? slots.find((slot) => slot.slotId === id) : undefined
  }, [slots, activeSlotId])

  const value = useMemo<RfqBundleValue>(
    () => ({
      slots,
      activeSlotId: effectiveActiveSlot?.slotId,
      beginDraft,
      activateDraft,
      discardSlot,
      setSlotRfqId,
      setSlotRfqNumber,
      setSlotCreating,
      clearSlot,
      persistError,

      items: effectiveActiveSlot?.items ?? [],
      addItems,
      removeItem,
      clear,
      draft: effectiveActiveSlot?.draft ?? EMPTY_RFQ_DRAFT_META,
      setDraft,
      hydrateSpecSelections,
      discard,
      draftRfqId: effectiveActiveSlot?.rfqId,
      setDraftRfqId,
      draftRfqNumber: effectiveActiveSlot?.rfqNumber,
      setDraftRfqNumber,
      creatingDraft: effectiveActiveSlot?.creating ?? false,
      setCreatingDraft,
      pricesResolving:
        effectiveActiveSlot !== undefined && resolvingSlotIds.has(effectiveActiveSlot.slotId),
    }),
    [
      slots,
      effectiveActiveSlot,
      beginDraft,
      activateDraft,
      discardSlot,
      setSlotRfqId,
      setSlotRfqNumber,
      setSlotCreating,
      clearSlot,
      addItems,
      removeItem,
      clear,
      setDraft,
      hydrateSpecSelections,
      discard,
      setDraftRfqId,
      setDraftRfqNumber,
      setCreatingDraft,
      resolvingSlotIds,
      persistError,
    ],
  )
  return <RfqBundleContext.Provider value={value}>{children}</RfqBundleContext.Provider>
}

/**
 * Tolerates being called with no provider mounted (returns an inert value),
 * matching `useCartCount`'s pattern — a component that reads the bundle
 * (e.g. `Sidebar`, or a vertical's page under test) never has to wrap the
 * provider just to render.
 */
export function useRfqBundle(): RfqBundleValue {
  return useContext(RfqBundleContext) ?? INERT
}

/**
 * UID-551 (ADR-0172 D4), CORRECTED by code review C1 — the ONE shared
 * predicate for "is this slot a draft the buyer can actually see and act
 * on?": a slot with **at least one queued part**.
 *
 * Why a predicate exists at all. ADR-0172 D4 originally specified
 * `slots.length` with "deliberately no derived helper/filter", on the theory
 * that any filter would be a second definition that could drift. That was
 * exactly backwards, and it caused a real bug: a slot with ZERO items renders
 * NO card on the Draft tab (`RfqDraftPanel`'s `partCount === 0` branch is the
 * tab-wide zero-state, not a card), so a raw `slots.length` counted something
 * the tab does not render — "Draft 1" above a zero-state. The way badge and
 * tab are kept in lock-step is that they apply the SAME filter, not that they
 * happen to call the same function name.
 *
 * So: this predicate is the shared definition of "a draft", and BOTH the
 * badges ({@link countOpenDrafts}, read by `Sidebar.tsx` and
 * `RfqListPage.tsx`) and the Draft tab's rendered card list
 * (`RfqListPage.tsx`) filter through it. If you add a third consumer, filter
 * through this — do not re-spell the condition, and do not go back to a bare
 * `slots.length`.
 */
export function isOpenDraftSlot(slot: RfqDraftSlot): boolean {
  return slot.items.length > 0
}

/**
 * UID-551 (ADR-0172 D4, as corrected by code review C1) — the ONE shared
 * computation behind both the Draft tab's own badge (`RfqListPage.tsx`) and
 * the Sidebar nav badge's draft addend (`Sidebar.tsx`): how many drafts this
 * session is holding that the Draft tab ACTUALLY RENDERS A CARD FOR.
 *
 * ADR-0167 Amendment 1's controlling principle (inherited by ADR-0172 D1) is
 * that the badge equals what the customer can see on screen. That is only
 * true if this count and the card list share {@link isOpenDraftSlot} — see
 * that predicate's doc comment for the bug the previous unfiltered
 * `slots.length` produced. Do not recompute this from `items`, from
 * `GET /rfq`, or from the persisted snapshot.
 */
export function countOpenDrafts(slots: readonly RfqDraftSlot[]): number {
  return slots.filter(isOpenDraftSlot).length
}

/** The `slotId` {@link INERT}'s no-op `beginDraft` reports. Code-review
 * follow-up: this used to be a freshly-minted `makeSlotId()` uuid, which
 * claimed a real slot had been created by a store that creates nothing — a
 * caller that threaded it into `setSlotRfqId`/`activateDraft` would be
 * addressing a slot that has never existed. A single recognizable constant
 * makes the no-provider case obvious in a debugger instead of looking like a
 * live slot. */
const INERT_SLOT_ID = 'inert-no-provider-slot'

const INERT: RfqBundleValue = {
  slots: [],
  activeSlotId: undefined,
  beginDraft: () => INERT_SLOT_ID,
  activateDraft: () => {},
  discardSlot: () => {},
  setSlotRfqId: () => {},
  setSlotRfqNumber: () => {},
  setSlotCreating: () => {},
  clearSlot: () => {},
  persistError: undefined,

  items: [],
  addItems: () => {},
  removeItem: () => {},
  clear: () => {},
  draft: EMPTY_RFQ_DRAFT_META,
  setDraft: () => {},
  hydrateSpecSelections: () => {},
  discard: () => {},
  draftRfqId: undefined,
  setDraftRfqId: () => {},
  draftRfqNumber: undefined,
  setDraftRfqNumber: () => {},
  creatingDraft: false,
  setCreatingDraft: () => {},
  pricesResolving: false,
}
